import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

/**
 * Export controls for data and visualization export
 * Supports multiple export formats and options
 */
const ExportControls = ({ 
  onExportCSV, 
  onExportPDF, 
  onExportCharts, 
  isExporting = false 
}) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportScope, setExportScope] = useState('filtered');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeMetrics, setIncludeMetrics] = useState(true);

  const handleExport = () => {
    switch (exportFormat) {
      case 'csv':
        onExportCSV();
        break;
      case 'pdf':
        onExportPDF();
        break;
      case 'excel':
        // TODO: Implement Excel export
        console.log('Excel export not yet implemented');
        break;
      case 'json':
        // TODO: Implement JSON export
        console.log('JSON export not yet implemented');
        break;
      default:
        onExportCSV();
    }
  };

  const exportOptions = [
    { value: 'csv', label: 'CSV Spreadsheet', description: 'Compatible with Excel and Google Sheets' },
    { value: 'pdf', label: 'PDF Report', description: 'Complete report with charts and analysis' },
    { value: 'excel', label: 'Excel Workbook', description: 'Multi-sheet Excel file with formulas' },
    { value: 'json', label: 'JSON Data', description: 'Raw data for external analysis tools' }
  ];

  const scopeOptions = [
    { value: 'filtered', label: 'Filtered Data Only', description: 'Export only currently filtered results' },
    { value: 'all', label: 'All Data', description: 'Export complete historical dataset' },
    { value: 'selected', label: 'Selected Races', description: 'Export only manually selected races' },
    { value: 'strategy', label: 'Strategy Results', description: 'Export strategy application results only' }
  ];

  return (
    <div className="export-controls">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Export Data</h3>
          <p className="text-sm text-gray-600">
            Export analysis results and visualizations in various formats
          </p>
        </div>
        <Badge variant="outline" className="text-xs">
          {isExporting ? 'Exporting...' : 'Ready to Export'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Quick Export Buttons */}
        <Card className="p-4">
          <h4 className="font-medium mb-3">Quick Export</h4>
          <div className="space-y-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={onExportCSV}
              disabled={isExporting}
              className="w-full justify-start"
            >
              📊 Export CSV
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={onExportPDF}
              disabled={isExporting}
              className="w-full justify-start"
            >
              📄 Export PDF
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={onExportCharts}
              disabled={isExporting}
              className="w-full justify-start"
            >
              📈 Export Charts
            </Button>
          </div>
        </Card>

        {/* Export Format Selection */}
        <Card className="p-4">
          <h4 className="font-medium mb-3">Export Format</h4>
          <Select value={exportFormat} onValueChange={setExportFormat}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              {exportOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Card>

        {/* Export Scope Selection */}
        <Card className="p-4">
          <h4 className="font-medium mb-3">Export Scope</h4>
          <Select value={exportScope} onValueChange={setExportScope}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select scope" />
            </SelectTrigger>
            <SelectContent>
              {scopeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Card>

        {/* Export Options */}
        <Card className="p-4">
          <h4 className="font-medium mb-3">Include</h4>
          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={includeCharts}
                onChange={(e) => setIncludeCharts(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Charts & Visualizations</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={includeMetrics}
                onChange={(e) => setIncludeMetrics(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Performance Metrics</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                defaultChecked={true}
                className="rounded"
              />
              <span className="text-sm">Race Details</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                defaultChecked={false}
                className="rounded"
              />
              <span className="text-sm">Strategy Configuration</span>
            </label>
          </div>
        </Card>
      </div>

      <Separator className="my-4" />

      {/* Advanced Export Options */}
      <Card className="p-4">
        <h4 className="font-medium mb-3">Advanced Options</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Date Format</label>
            <Select defaultValue="iso">
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="iso">ISO (YYYY-MM-DD)</SelectItem>
                <SelectItem value="au">Australian (DD/MM/YYYY)</SelectItem>
                <SelectItem value="us">US (MM/DD/YYYY)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Currency Format</label>
            <Select defaultValue="aud">
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aud">Australian Dollar (AUD)</SelectItem>
                <SelectItem value="usd">US Dollar (USD)</SelectItem>
                <SelectItem value="number">Number Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Decimal Places</label>
            <Select defaultValue="2">
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0 (Whole numbers)</SelectItem>
                <SelectItem value="1">1 decimal place</SelectItem>
                <SelectItem value="2">2 decimal places</SelectItem>
                <SelectItem value="4">4 decimal places</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Separator className="my-4" />

      {/* Export Action */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Selected format: <strong>{exportOptions.find(o => o.value === exportFormat)?.label}</strong>
          <br />
          Export scope: <strong>{scopeOptions.find(o => o.value === exportScope)?.label}</strong>
        </div>
        <Button 
          onClick={handleExport}
          disabled={isExporting}
          className="px-6"
        >
          {isExporting ? 'Exporting...' : 'Export Data'}
        </Button>
      </div>
    </div>
  );
};

export default ExportControls;